import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Smile, X, Clock3 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { HubAvatar } from "@/components/social/HubAvatar";
import { HubAuthorDisplay, type HubAuthor } from "@/components/social/HubAuthorDisplay";
import { useHubSettings } from "@/hooks/hub/useHubSettings";
import { useHubIdentities } from "@/hooks/hub/useHubIdentities";
import { useHubComments, MAX_HUB_COMMENT, type HubComment } from "@/hooks/hub/useHubComments";
import { HubItemMenu } from "@/components/social/HubItemMenu";
import { HubInlineEditor } from "@/components/social/HubInlineEditor";
import { useAuth } from "@/contexts/AuthContext";
import { relativeTime } from "@/lib/hub/relativeTime";


/**
 * LR HUB comment section — expands DOWN under a post (Facebook/LinkedIn
 * style), one level of replies.
 *
 * Bodies render as PLAIN TEXT (React escaping); never dangerouslySetInnerHTML.
 * Every write goes through `moderate-hub-comment` (see useHubComments) — the
 * client has no INSERT grant on hub_post_comments.
 */

const EMOJIS = [
  "🙂", "👍", "🙌", "🔥", "✅", "❗", "🚛", "🚚", "❄️", "📦",
  "📍", "📈", "📉", "⏱️", "🤝", "💪", "🎯", "🗺️", "🧊", "🙏",
];

function CommentComposer({
  placeholder,
  submitting,
  autoFocus,
  onSubmit,
  onCancel,
}: {
  placeholder: string;
  submitting?: boolean;
  autoFocus?: boolean;
  onSubmit: (body: string) => void;
  onCancel?: () => void;
}) {
  const { data: settings } = useHubSettings();
  const [body, setBody] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus) taRef.current?.focus();
  }, [autoFocus]);

  const len = body.trim().length;
  const nearLimit = len >= MAX_HUB_COMMENT - 40;
  const atLimit = len >= MAX_HUB_COMMENT;
  const canPost = len > 0 && !submitting;

  function insertEmoji(emoji: string) {
    const el = taRef.current;
    if (!el) {
      setBody((b) => (b + emoji).slice(0, MAX_HUB_COMMENT));
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    setBody((body.slice(0, start) + emoji + body.slice(end)).slice(0, MAX_HUB_COMMENT));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  }

  function send() {
    if (!canPost) return;
    onSubmit(body.trim());
    setBody("");
  }

  return (
    <div className="flex items-start gap-2">
      <HubAvatar
        username={settings?.display_name || "LR"}
        avatarPath={settings?.avatar_status === "approved" ? settings.avatar_path : null}
        size="sm"
      />
      <div className="min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 py-2">
        <textarea
          ref={taRef}
          value={body}
          onChange={(e) => setBody(e.target.value.slice(0, MAX_HUB_COMMENT))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={1}
          placeholder={placeholder}
          className="min-h-[34px] w-full resize-y bg-transparent text-[12px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        <div className="mt-1 flex items-center justify-between gap-2">
          <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label="Insert emoji"
                className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:text-brand-teal-deep"
              >
                <Smile className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[228px] p-2">
              <div className="grid grid-cols-5 gap-1">
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    onClick={() => {
                      insertEmoji(e);
                      setEmojiOpen(false);
                    }}
                    className="rounded-md py-1 text-[16px] hover:bg-muted"
                  >
                    {e}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-[10px] tabular-nums",
                atLimit ? "font-bold text-danger" : nearLimit ? "font-semibold text-brand-amber" : "text-muted-foreground",
              )}
            >
              {len}/{MAX_HUB_COMMENT}
            </span>
            {onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              disabled={!canPost}
              onClick={send}
              className={cn(
                "inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11.5px] font-bold transition-colors",
                canPost
                  ? "bg-brand-teal-deep text-white hover:bg-brand-teal-deep/85"
                  : "cursor-not-allowed bg-muted text-muted-foreground",
              )}
            >
              {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
              Comment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentRow({
  comment,
  author,
  indented,
  isOwn,
  saving,
  onReply,
  onEdit,
  onDeleted,
}: {
  comment: HubComment;
  author: HubAuthor;
  indented?: boolean;
  isOwn: boolean;
  saving?: boolean;
  onReply?: () => void;
  onEdit: (body: string) => Promise<boolean>;
  onDeleted: () => void;
}) {
  const inReview = comment.moderation_status !== "approved";
  const [editing, setEditing] = useState(false);
  return (
    <div className={cn("animate-fade-in", indented && "ml-9 border-l border-border pl-3")}>
      <HubAuthorDisplay
        author={author}
        size="sm"
        timeLabel={`${relativeTime(comment.created_at)}${comment.edited_at ? " · edited" : ""}`}
        trailing={
          <HubItemMenu
            kind="comment"
            targetId={comment.id}
            isOwn={isOwn}
            authorId={comment.author_id}
            authorLabel={author?.hubIdentity?.handle ? `@${author.hubIdentity.handle}` : author?.name}
            onEdit={() => setEditing(true)}
            onDeleted={onDeleted}
          />
        }
      />
      {editing ? (
        <div className="ml-[42px]">
          <HubInlineEditor
            initial={comment.body}
            max={MAX_HUB_COMMENT}
            saving={saving}
            onCancel={() => setEditing(false)}
            onSave={async (body) => {
              const ok = await onEdit(body);
              if (ok) setEditing(false);
            }}
          />
        </div>
      ) : (
        <p className="ml-[42px] mt-1 whitespace-pre-wrap break-words text-[12px] leading-relaxed text-foreground">
          {comment.body}
        </p>
      )}
      <div className="ml-[42px] mt-1 flex items-center gap-3">
        {onReply && !editing ? (
          <button
            type="button"
            onClick={onReply}
            className="text-[11px] font-semibold text-muted-foreground hover:text-brand-teal-deep"
          >
            Reply
          </button>
        ) : null}
        {inReview ? (
          <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-brand-amber">
            <Clock3 className="h-3 w-3" />
            {comment.moderation_status === "rejected"
              ? "Not approved — only you can see this"
              : "Pending approval — only you can see this"}
          </span>
        ) : null}

      </div>
    </div>
  );
}


export function HubComments({ postId, open }: { postId: string; open: boolean }) {
  const c = useHubComments(postId, open);
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [replyTo, setReplyTo] = useState<string | null>(null);


  const ids = c.threads.flatMap((t) => [t.comment.author_id, ...t.replies.map((r) => r.author_id)]);
  const identities = useHubIdentities(ids);

  const authorFor = (userId: string): HubAuthor => {
    const identity = identities.map.get(userId) ?? null;
    return { id: userId, name: identity?.display_name || "LR Member", hubIdentity: identity };
  };

  if (!open) return null;

  return (
    <div className="mt-3 animate-fade-in space-y-3 border-t border-border pt-3">
      {c.isLoading ? (
        <div className="flex h-12 items-center justify-center">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        </div>
      ) : c.isError ? (
        <p className="text-[11.5px] text-muted-foreground">Could not load comments.</p>
      ) : c.threads.length === 0 ? (
        <p className="text-[11.5px] text-muted-foreground">No comments yet — start the conversation.</p>
      ) : (
        <div className="space-y-3">
          {c.threads.map((t) => (
            <div key={t.comment.id} className="space-y-2.5">
              <CommentRow
                comment={t.comment}
                author={authorFor(t.comment.author_id)}
                isOwn={t.comment.author_id === userId}
                saving={c.posting}
                onReply={() => setReplyTo(replyTo === t.comment.id ? null : t.comment.id)}
                onEdit={(body) => c.edit(t.comment.id, body)}
                onDeleted={() => c.removeLocal(t.comment.id)}
              />
              {t.replies.map((r) => (
                <CommentRow
                  key={r.id}
                  comment={r}
                  author={authorFor(r.author_id)}
                  indented
                  isOwn={r.author_id === userId}
                  saving={c.posting}
                  onEdit={(body) => c.edit(r.id, body)}
                  onDeleted={() => c.removeLocal(r.id)}
                />
              ))}

              {replyTo === t.comment.id ? (
                <div className="ml-9">
                  <CommentComposer
                    autoFocus
                    placeholder="Write a reply…"
                    submitting={c.posting}
                    onCancel={() => setReplyTo(null)}
                    onSubmit={async (body) => {
                      const ok = await c.submit(body, t.comment.id);
                      if (ok) setReplyTo(null);
                    }}
                  />
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {c.error || c.notice ? (
        <div
          className={cn(
            "flex items-start justify-between gap-2 rounded-lg border px-3 py-2 text-[11.5px]",
            c.error
              ? "border-danger/30 bg-danger/10 text-danger"
              : "border-brand-amber/40 bg-brand-amber/10 text-foreground",
          )}
        >
          <span>{c.error || c.notice}</span>
          <button type="button" onClick={c.clearNotices}>
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : null}

      <CommentComposer
        placeholder="Add a comment…"
        submitting={c.posting}
        onSubmit={(body) => c.submit(body, null)}
      />
    </div>
  );
}

export default HubComments;
